import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useMedia } from 'react-use';

import OutsideContainer from '../../components/OutsideContainer';
import AnswerButtons from '../../../../components/AnswerButtons';
import { DifficultyLevel } from '../../../../shared/enums';
import { getConfigByDifficulty } from '../../helpers';
import { minDesktopWidth } from '../../../../shared/widths';
import { useAppSelector } from '../../../../core/redux/hooks/useAppSelector';
import { selectDifficulty, selectGameType } from '../../../game/selectors';
import WinSound from '../../../../assets/sounds/win-sound.wav';
import LooseSound from '../../../../assets/sounds/loose-sound.wav';

import styles from './styles.module.scss';
import { useExecuteGameEndMutation, useExecuteGameStartMutation } from '../../../game/services/GameService';
import { difficultyToCompletedLevelKey, difficultyToNumericLevel } from '../../../game/helpers';
import { preloadAudio, unlockAudioContext } from '../../../../shared/audioManager';
import { useIsLandscape } from '../../../../hooks/useIsLandscape';
import { useGameExitWatcher } from '../../../game/hooks/useGameExitWatcher';
import { API_VERSION } from '../../../../core/redux/store/api/constants';
import { useWinEvent } from '../../../../hooks/useWinEvent';
import { useGameData } from '../../../game/hooks/useGameData';

const TIME_TO_VIEW_FINISH_SCREEN = 2000;

const MetroGamePage: React.FC = () => {
  const gameType = useAppSelector(selectGameType);
  const difficulty = useAppSelector(selectDifficulty);
  const isLandscape = useIsLandscape();
  const { userData } = useGameData();
  const userId = userData?.id || null;

  useGameExitWatcher({
    url: `/${API_VERSION}/game/event/execute`,
    getPayload: () => ({
      gameCode: gameType,
      properties: { level: difficultyToNumericLevel[level] as unknown as object },
      eventId: 'end',
    }),
  });

  const [executeGameStartEvent] = useExecuteGameStartMutation();
  const [executeGameEndEvent] = useExecuteGameEndMutation();

  const isDesktop = useMedia(`(min-width: ${minDesktopWidth}px)`, true);

  const { time, level, totalLevels, groupAppearanceFrequency, groupSizeRange, groupMovements, groupExitTime, offsetAnswer } =
    useMemo(() => {
      return getConfigByDifficulty(difficulty as DifficultyLevel);
    }, [difficulty]);

  const { sendWinEvent } = useWinEvent({
    gameCode: gameType,
    eventId: difficultyToCompletedLevelKey[level],
    userId,
  });

  const calculatedGroupExitTime = isDesktop ? groupExitTime + 1 : groupExitTime;

  const [peopleInMetro, setPeopleInMetro] = useState(0);
  const [correctAnswer, setCorrectAnswer] = useState<number | null>(null);
  const [currentLevel, setCurrentLevel] = useState(1);

  const [isProgressOnPause, setIsProgressOnPause] = useState(true);
  const [isGameOnPause, setIsGameOnPause] = useState(false);
  const prevIsProgressOnPauseRef = useRef<boolean>(true);
  const prevIsGameOnPauseRef = useRef<boolean>(false);

  const isActiveQuestion = typeof correctAnswer === 'number';
  const activeQuestionRef = useRef<boolean>(isActiveQuestion);
  useEffect(() => {
    activeQuestionRef.current = isActiveQuestion;
  }, [isActiveQuestion]);

  const navigate = useNavigate();

  const loseScreenUrl = `/lost`;
  const winScreenUrl = `/win`;

  useEffect(() => {
    unlockAudioContext();
    preloadAudio(WinSound);
    preloadAudio(LooseSound);
  }, []);

  useEffect(() => {
    sendStartEvent();
  }, []);

  useEffect(() => {
    if (isLandscape) {
      prevIsGameOnPauseRef.current = isGameOnPause;
      prevIsProgressOnPauseRef.current = isProgressOnPause;

      setIsGameOnPause(true);
      setIsProgressOnPause(true);
    } else {
      setIsGameOnPause(prevIsGameOnPauseRef.current);
      setIsProgressOnPause(prevIsProgressOnPauseRef.current);
    }
  }, [isLandscape]);

  const sendStartEvent = async () => {
    try {
      const result = await executeGameStartEvent({
        gameCode: gameType,
        properties: { level: difficultyToNumericLevel[level] as unknown as object },
      }).unwrap();
    } catch (err) {
      console.error('Failed to execute event:', err);
    }
  };

  const sendEndEvent = async () => {
    try {
      const result = await executeGameEndEvent({
        gameCode: gameType,
        properties: { level: difficultyToNumericLevel[level] as unknown as object },
      }).unwrap();
    } catch (err) {
      console.error('Failed to execute event:', err);
    }
  };

  const handleChangePeopleInMetro = (value: number) => {
    setIsGameOnPause(false);
    setIsProgressOnPause(false);
    setCorrectAnswer(value);

    prevIsGameOnPauseRef.current = false;
    prevIsProgressOnPauseRef.current = false;
  };

  const handleClickCorrect = () => {
    setIsGameOnPause(false);
    setIsProgressOnPause(true);

    setTimeout(() => {
      if (currentLevel === totalLevels) {
        sendEndEvent();
        sendWinEvent();
        navigate(winScreenUrl);
        return;
      }

      typeof correctAnswer === 'number' && setPeopleInMetro(correctAnswer);
      setCurrentLevel((prev) => prev + 1);
      setCorrectAnswer(null);
    }, TIME_TO_VIEW_FINISH_SCREEN);
  };

  const handleClose = () => {
    sendEndEvent();
    navigate(`/${gameType}/difficulty`);
  };

  const handleClickWrong = () => {
    sendEndEvent();
    setIsGameOnPause(true);

    setTimeout(() => {
      navigate(loseScreenUrl);
    }, TIME_TO_VIEW_FINISH_SCREEN);
  };

  const handleEndTime = () => {
    sendEndEvent();
    navigate(loseScreenUrl);
  };

  const handleOnRulesModalToggle = useCallback(
    (state: boolean) => {
      setIsGameOnPause(state);
      if (activeQuestionRef.current) {
        setIsProgressOnPause(state);
      }
    },
    [setIsGameOnPause, setIsProgressOnPause],
  );

  return (
    <div className={classNames(styles.page, 'base-page')}>
      <OutsideContainer
        className={styles.outside}
        close={handleClose}
        currentLevel={currentLevel}
        groupAppearanceFrequency={groupAppearanceFrequency}
        groupExitTime={calculatedGroupExitTime}
        groupMovements={groupMovements}
        groupSizeRange={groupSizeRange}
        onEndTime={handleEndTime}
        onChangePeopleInMetro={handleChangePeopleInMetro}
        peopleInMetro={peopleInMetro}
        time={time}
        isProgressOnPause={isProgressOnPause}
        isGameOnPause={isGameOnPause}
        totalLevels={totalLevels}
        isActiveQuestion={isActiveQuestion}
        onRulesModalToggle={handleOnRulesModalToggle}
      />

      <div className={styles.underground}>
        <AnswerButtons
          className={styles.answerButtons}
          correctAnswer={correctAnswer}
          onClickCorrect={handleClickCorrect}
          onClickWrong={handleClickWrong}
          offsetAnswer={offsetAnswer}
        />
      </div>
    </div>
  );
};

export default MetroGamePage;
